/**
 * Vue Router 配置
 * 
 * 工作台路由结构：
 * - /login - 登录页
 * - / - 工作台布局（包含子路由）
 *   - /dashboard - 仪表盘
 *   - /graduate/* - 研究生工作（组会面板 / 工作任务，模块路由注入）
 *   - /profile - 个人档案
 *   - /gallery - 图床管理
 *   - /tasks - 自动化任务
 *   - /subsystems - 子系统管理
 *   - /settings - 系统设置
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { moduleRegistry } from '@/modules'
import { setupRouterGuards } from './guards'

/** 基础路由（不依赖模块注册） */
const baseRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/modules/auth/views/LoginView.vue'),
    meta: { title: '登录', public: true }
  },
  {
    path: '/',
    component: () => import('@/layouts/WorkbenchLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'home',
        redirect: { name: 'dashboard' }
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/modules/dashboard/views/DashboardView.vue'),
        meta: { title: '仪表盘' }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: '页面未找到' }
  }
]

/** 创建路由实例 */
const router = createRouter({
  history: createWebHistory(),
  routes: baseRoutes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

/**
 * 顶层路由路径（不在工作台布局内渲染的页面）
 * 模块若重复声明这些路径，必须保持顶层，不能被注入到工作台布局中
 */
const TOP_LEVEL_PATHS = new Set(
  baseRoutes.filter((route) => !route.children).map((route) => route.path)
)

/**
 * 动态注入模块路由
 * 在模块注册完成后调用此函数，将所有模块的路由注入到 / 的 children 中
 */
export function injectModuleRoutes(): void {
  const moduleRoutes = moduleRegistry.collectRoutes()
  const layoutRoute = router.getRoutes().find((r) => r.path === '/')
  let injected = 0

  if (layoutRoute) {
    moduleRoutes.forEach((route) => {
      // 登录页等顶层路由已独立注册，若再注入布局会渲染成「工作台外壳 + 页面」的嵌套结构
      if (TOP_LEVEL_PATHS.has(route.path)) {
        console.info(`[Router] 路由 ${route.path} 保持顶层渲染，跳过布局注入`)
        return
      }

      router.addRoute('home', route)
      injected += 1
    })
  }

  console.info(`[Router] 已注入 ${injected} 条模块路由`)
}

/** 设置路由守卫 */
setupRouterGuards(router)

export default router
